import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";
const API_URL_IMAGE =
  process.env.REACT_APP_API_URL_IMAGE || "http://127.0.0.1:8000";
const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === "true";

const DEFAULT_TIMEOUT = 15000;
const UPLOAD_TIMEOUT = 60000;

// ✅ Configuration correcte avec withCredentials
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // ✅ Envoie automatiquement les cookies
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: DEFAULT_TIMEOUT,
});

/**
 * ✅ Fonction simplifiée : pas de token à gérer manuellement
 * Le cookie est géré automatiquement par le navigateur
 */
export const clearSession = () => {
  if (DEBUG_MODE) console.log("⚠️ clearSession: Session cleared");
  // Pas besoin de supprimer le token, le backend gère le cookie
};

/**
 * ✅ Vérifier si l'utilisateur est authentifié
 * On ne peut pas vérifier le cookie HTTP-only côté JS,
 * donc on fait une requête au backend
 */
export const isAuthenticated = async () => {
  try {
    await axiosInstance.get("/profile");
    return true;
  } catch {
    return false;
  }
};

/**
 * Construire l'URL des images
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return "/emogiesnack.png";
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  const baseUrl = API_URL_IMAGE.replace(/\/$/, "");
  const path = imagePath.replace(/^\/+/, "");
  return `${baseUrl}/${path}`;
};

// ❌ SUPPRIMÉ : Plus besoin d'intercepteur request
// Le cookie est envoyé automatiquement

// ✅ Intercepteur response pour gérer les erreurs
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (DEBUG_MODE) {
      console.error("❌ Axios Response Error:", error.response || error);
    }

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // ✅ Session expirée : rediriger vers login
          if (DEBUG_MODE) console.log("⚠️ 401 Unauthorized");

          // ✅ NE PAS rediriger si on est en train de charger le profil initial
          // (évite la redirection pendant le useEffect de AuthContext)
          const isProfileRequest = error.config.url?.includes("/profile");
          const isLoginPage = window.location.pathname === "/login";
          const isRegisterPage = window.location.pathname === "/register";

          // ✅ LISTE DES PAGES PROTÉGÉES qui ne doivent PAS rediriger automatiquement
          // Car l'utilisateur vient peut-être de se connecter/s'inscrire
          const protectedPagesWhitelist = [
            "/profile",
            "/my-bookings",
            "/booking"
          ];
          const currentPath = window.location.pathname;
          const isOnProtectedPage = protectedPagesWhitelist.some(page => currentPath.includes(page));

          // ✅ Rediriger uniquement si :
          // - Ce n'est PAS la requête de chargement initial du profil
          // - On n'est PAS déjà sur la page login/register
          // - On n'est PAS sur une page protégée (laisser la page gérer l'erreur)
          if (!isProfileRequest && !isLoginPage && !isRegisterPage && !isOnProtectedPage) {
            clearSession(); // Nettoyer la session
            window.location.href = "/login";
          }

          return Promise.reject(
            new Error(
              data.message || "Session expirée. Veuillez vous reconnecter."
            )
          );

        case 403:
          if (DEBUG_MODE) console.warn("🚫 403 Forbidden");
          return Promise.reject(
            new Error(data.message || "Accès refusé. Droits insuffisants.")
          );

        case 404:
          if (DEBUG_MODE) console.warn("🔍 404 Not Found");
          return Promise.reject(
            new Error(data.message || "Ressource non trouvée.")
          );

        case 422:
          if (DEBUG_MODE) console.warn("⚠️ 422 Validation Error:", data.errors);
          if (data.errors) {
            const messages = Object.values(data.errors).flat();
            return Promise.reject(new Error(messages.join(" ")));
          }
          return Promise.reject(
            new Error(data.message || "Erreur de validation.")
          );

        default:
          return Promise.reject(
            new Error(data.message || `Erreur serveur: ${status}`)
          );
      }
    } else if (error.request) {
      if (DEBUG_MODE) console.error("❌ No response:", error.request);
      return Promise.reject(
        new Error("Aucune réponse du serveur. Vérifiez votre connexion.")
      );
    } else {
      if (DEBUG_MODE) console.error("❌ Request error:", error.message);
      return Promise.reject(new Error(`Erreur de requête: ${error.message}`));
    }
  }
);

/**
 * Upload de fichiers avec timeout étendu
 */
export const uploadFile = (url, formData) => {
  if (DEBUG_MODE) console.log("🚀 uploadFile:", url);
  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: UPLOAD_TIMEOUT,
  });
};

export default axiosInstance;
